import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { RevealSection, RevealText, PullQuoteLight } from '@/components/shared/Reveal';

const IMAGES = {
  table: "https://images.unsplash.com/photo-1716850724755-21a55ab68f25?w=1600&q=80&auto=format",
  interior: "https://images.unsplash.com/photo-1668089677938-b52086753f77?w=1400&q=80&auto=format",
  glasses: "https://images.unsplash.com/photo-1619460850041-77a2b97729aa?w=1200&q=80&auto=format",
};

const SectionLabel = ({ children }) => (
  <span className="section-label block mb-5">{children}</span>
);

const BulletList = ({ items, className = "" }) => (
  <div className={`space-y-2.5 ${className}`}>
    {items.map((item, i) => (
      <div key={i} className="flex items-start gap-3">
        <div className="w-1 h-1 rounded-full bg-copper mt-2.5 flex-shrink-0" />
        <p className="text-vine-primary/65 text-[15px] leading-relaxed">{item}</p>
      </div>
    ))}
  </div>
);

const BulletListLight = ({ items, className = "" }) => (
  <div className={`space-y-2.5 ${className}`}>
    {items.map((item, i) => (
      <div key={i} className="flex items-start gap-3">
        <div className="w-1 h-1 rounded-full bg-copper mt-2.5 flex-shrink-0" />
        <p className="text-vine-neutral/70 text-[15px] leading-relaxed">{item}</p>
      </div>
    ))}
  </div>
);

export default function ExecutiveDinnersPage() {
  return (
    <div>
      {/* ===== HERO ===== */}
      <section className="bg-vine-deep text-vine-warm relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.table} alt="" className="w-full h-full object-cover editorial-img opacity-25" loading="eager" />
          <div className="absolute inset-0 bg-vine-deep/60" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-32 md:py-44 relative z-10">
          <div className="max-w-3xl">
            <RevealText>
              <div className="w-14 h-px bg-copper mb-12" />
            </RevealText>
            <RevealText delay={50}>
              <SectionLabel>Executive Dinners</SectionLabel>
            </RevealText>
            <RevealText delay={150}>
              <h1 className="font-display text-4xl md:text-5xl lg:text-[5.5rem] font-light leading-[1.05] tracking-tight mt-4">
                The Operator Table.
              </h1>
            </RevealText>
            <RevealText delay={300}>
              <p className="mt-10 text-lg md:text-xl text-vine-neutral/70 leading-relaxed max-w-xl font-light">
                Structured proximity for operators whose decisions carry consequences.
              </p>
            </RevealText>
          </div>
        </div>
      </section>

      {/* ===== OPENING STATEMENTS ===== */}
      <section className="bg-vine-warm section-spacer-lg">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <RevealSection>
            <div className="space-y-3">
              <p className="text-vine-primary/50 text-[15px] leading-relaxed">The Executive Dinner series is not a networking event.</p>
              <p className="text-vine-primary/50 text-[15px] leading-relaxed">It is not a social gathering.</p>
              <p className="text-vine-primary/50 text-[15px] leading-relaxed">It is not open access.</p>
            </div>
          </RevealSection>
          <RevealSection delay={200}>
            <p className="mt-12 font-display text-2xl md:text-3xl text-vine-primary font-light">
              It is a curated strategic table.
            </p>
          </RevealSection>
        </div>
      </section>

      {/* ===== WHY THIS TABLE EXISTS ===== */}
      <section className="bg-vine-deep text-vine-warm section-spacer-lg">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <RevealSection>
            <SectionLabel>Why This Table Exists</SectionLabel>
            <h2 className="font-display text-3xl md:text-4xl font-light leading-tight mt-4">
              Most leaders operate in isolation.
            </h2>
          </RevealSection>

          <RevealSection delay={150}>
            <div className="mt-14">
              <p className="text-vine-neutral/60 text-[14px] font-medium mb-5">They have:</p>
              <BulletListLight items={[
                "Advisors who report to them",
                "Teams who depend on them",
                "Investors who expect from them"
              ]} />
            </div>
          </RevealSection>

          <RevealSection delay={200}>
            <p className="mt-12 text-vine-neutral/60 text-[15px] leading-relaxed max-w-xl">
              But very few spaces where they can think in real time with peers navigating comparable pressure.
            </p>

            <p className="mt-8 text-vine-warm/80 text-[15px] font-medium">
              The Operator Table exists for that reason.
            </p>
          </RevealSection>

          <RevealSection delay={250}>
            <div className="mt-14 pl-6 border-l-2 border-copper/30 space-y-3">
              <p className="text-vine-neutral/50 text-[15px]">Signal, not spectacle.</p>
              <p className="text-vine-neutral/50 text-[15px]">Depth, not introductions.</p>
              <p className="text-vine-neutral/50 text-[15px]">Confidentiality, not content.</p>
            </div>
          </RevealSection>
        </div>
      </section>

      {/* ===== THE FORMAT ===== */}
      <section className="bg-vine-warm section-spacer-lg">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-28 md:pt-36">
          <RevealSection>
            <SectionLabel>The Format</SectionLabel>
            <h2 className="font-display text-3xl md:text-4xl text-vine-primary font-light leading-tight mt-4">
              Five governing principles.
            </h2>
          </RevealSection>
        </div>

        {/* Structured Proximity */}
        <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-20">
          <div className="border-t border-vine-neutral/15 pt-14">
            <h3 className="font-display text-2xl md:text-[28px] text-vine-primary font-normal">
              Structured Proximity, Not Networking
            </h3>
            <div className="mt-8 space-y-4">
              <p className="text-vine-primary/70 text-[15px] leading-relaxed">
                Each dinner is a curated table of 8–12 operators.
              </p>
            </div>
            <div className="mt-8 pl-5 border-l border-vine-neutral/20 space-y-2">
              <p className="text-vine-primary/45 text-[14px]">No sponsors.</p>
              <p className="text-vine-primary/45 text-[14px]">No panels.</p>
              <p className="text-vine-primary/45 text-[14px]">No sales pitches.</p>
              <p className="text-vine-primary/45 text-[14px]">No audience.</p>
            </div>
            <div className="mt-8 space-y-2">
              <p className="text-vine-primary/55 text-[15px]">This is not about exchanging business cards.</p>
              <p className="text-vine-primary/70 text-[15px] font-medium">It is about sharpening perspective.</p>
            </div>
          </div>
        </div>

        {/* Curated Operator Table */}
        <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-20">
          <div className="border-t border-vine-neutral/15 pt-14">
            <h3 className="font-display text-2xl md:text-[28px] text-vine-primary font-normal">
              Curated Operator Table
            </h3>
            <p className="mt-8 text-vine-primary/70 text-[15px] leading-relaxed">
              Every seat is intentional.
            </p>
            <div className="mt-8">
              <p className="text-vine-primary/70 text-[14px] font-medium mb-3">Attendees typically include:</p>
              <BulletList items={[
                "Revenue-generating founders",
                "Executives building or restructuring ventures",
                "Product leaders navigating scale",
                "Capital-adjacent operators"
              ]} />
            </div>
            <p className="mt-8 text-vine-primary/50 text-[14px] italic">
              We curate for stage, seriousness, and alignment — not status.
            </p>
            <div className="mt-6 space-y-2">
              <p className="text-vine-primary/55 text-[15px]">You are not attending a room of spectators.</p>
              <p className="text-vine-primary/70 text-[15px] font-medium">You are seated among decision-makers.</p>
            </div>
          </div>
        </div>

        {/* Structured Salon Format */}
        <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-20">
          <div className="border-t border-vine-neutral/15 pt-14">
            <h3 className="font-display text-2xl md:text-[28px] text-vine-primary font-normal">
              Structured Salon Format
            </h3>
            <p className="mt-8 text-vine-primary/70 text-[15px] leading-relaxed">
              Each dinner follows a guided structure.
            </p>
            <div className="mt-8 space-y-4">
              {['Opening framing', 'Thematic discussion', 'Facilitated depth', 'Commitment and ask'].map((item, i) => (
                <div key={i} className="flex items-center gap-4">
                  <span className="text-[11px] text-copper tracking-widest font-medium w-6">0{i + 1}</span>
                  <p className="text-vine-primary/65 text-[15px]">{item}</p>
                </div>
              ))}
            </div>
            <p className="mt-8 text-vine-primary/50 text-[14px]">
              This prevents drift into small talk.
            </p>
            <p className="mt-3 text-vine-primary/50 text-[14px] italic">
              The conversation is designed to produce insight — not performance.
            </p>
          </div>
        </div>

        {/* Chatham House Rule */}
        <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-20">
          <div className="border-t border-vine-neutral/15 pt-14">
            <h3 className="font-display text-2xl md:text-[28px] text-vine-primary font-normal">
              Chatham House Rule
            </h3>
            <p className="mt-8 text-vine-primary/70 text-[15px] leading-relaxed font-medium">
              Confidentiality is non-negotiable.
            </p>
            <div className="mt-6 pl-5 border-l border-vine-neutral/20 space-y-2">
              <p className="text-vine-primary/55 text-[14px]">What is shared stays in the room.</p>
              <p className="text-vine-primary/55 text-[14px]">Attribution never leaves the table.</p>
            </div>
            <p className="mt-6 text-vine-primary/50 text-[14px] italic">
              This is what allows the conversation to matter.
            </p>
          </div>
        </div>

        {/* Global Series */}
        <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-20 pb-24 md:pb-32">
          <div className="border-t border-vine-neutral/15 pt-14">
            <h3 className="font-display text-2xl md:text-[28px] text-vine-primary font-normal">
              Global Series
            </h3>
            <p className="mt-8 text-vine-primary/70 text-[15px] leading-relaxed">
              Executive Dinners are hosted in select cities throughout the year.
            </p>
            <div className="mt-8">
              <p className="text-vine-primary/70 text-[14px] font-medium mb-3">Cities are chosen based on:</p>
              <BulletList items={[
                "Member density",
                "Strategic hubs",
                "Operator demand"
              ]} />
            </div>
            <div className="mt-8 pl-5 border-l border-copper/30">
              <p className="text-vine-primary/50 text-[14px]">
                The experience remains consistent: Intimate. Facilitated. Governed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== PHOTO BREAK ===== */}
      <div className="relative h-[240px] md:h-[320px] overflow-hidden">
        <img src={IMAGES.interior} alt="Architectural interior" className="w-full h-full object-cover editorial-img" loading="lazy" />
        <div className="absolute inset-0 bg-vine-deep/30" />
      </div>

      {/* ===== THE DINNER PASSPORT ===== */}
      <section className="bg-vine-deep text-vine-warm section-spacer-lg">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 section-spacer-lg">
          <SectionLabel>The Dinner Passport</SectionLabel>
          <h2 className="font-display text-3xl md:text-4xl font-light leading-tight mt-4">
            Structured Access for Advisory Circle Members
          </h2>

          <div className="mt-10 space-y-4">
            <p className="text-vine-neutral/70 text-[15px] leading-relaxed">
              The Dinner Passport is available as part of the Advisory Circle + Dinner Passport membership.
            </p>
            <p className="text-vine-neutral/70 text-[15px] leading-relaxed">
              It provides structured access to the global dinner series with guaranteed seating logic.
            </p>
          </div>

          <div className="mt-6 space-y-2">
            <p className="text-vine-neutral/50 text-[14px]">This is not unlimited access without boundaries.</p>
            <p className="text-vine-warm/80 text-[14px] font-medium">It is priority access with governance.</p>
          </div>

          {/* Passport Includes */}
          <div className="mt-16 pt-10 border-t border-white/[0.08]">
            <p className="text-copper text-[12px] tracking-[0.2em] uppercase font-medium mb-6">Passport Includes</p>
            <BulletListLight items={[
              "Priority RSVP access to all global Executive Dinners",
              "One guaranteed dinner seat annually in your designated city or regional hub",
              "Curated operator table placement",
              "Structured salon facilitation"
            ]} />
            <p className="mt-8 text-vine-neutral/40 text-[13px]">
              Included in the Advisory Circle + Dinner Passport tier at $22,000/year.
            </p>
          </div>
        </div>
      </section>

      {/* ===== ACCESS LOGIC ===== */}
      <section className="bg-vine-warm section-spacer-lg">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 section-spacer-lg">
          <SectionLabel>Access Logic</SectionLabel>
          <p className="text-vine-primary/60 text-[15px] mt-4 mb-14">Clarity prevents friction.</p>

          <div className="space-y-14">
            {[
              {
                step: "01",
                title: "Dinner Announced",
                lines: [
                  "City, date, and theme are shared with Passport holders first.",
                  "RSVP opens 21 days prior."
                ]
              },
              {
                step: "02",
                title: "Priority Window",
                lines: [
                  "Passport holders receive a 7-day priority RSVP window before Advisory Circle members without Passport access."
                ]
              },
              {
                step: "03",
                title: "Confirmation",
                lines: [
                  "Seating is confirmed 7 days prior to the event."
                ]
              },
              {
                step: "04",
                title: "City Guarantee",
                lines: [
                  "Each Passport holder is guaranteed at least one dinner seat per membership year in their designated city or regional hub (as defined by city cluster logic)."
                ]
              }
            ].map((item) => (
              <div key={item.step} className="border-t border-vine-neutral/15 pt-8">
                <div className="flex items-start gap-5">
                  <span className="text-copper text-[13px] tracking-[0.15em] font-medium mt-1">{item.step}</span>
                  <div>
                    <h3 className="font-display text-xl text-vine-primary font-normal mb-3">{item.title}</h3>
                    {item.lines.map((line, i) => (
                      <p key={i} className="text-vine-primary/60 text-[15px] leading-relaxed">{line}</p>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== WHAT THIS IS NOT ===== */}
      <section className="bg-vine-warm border-t border-vine-neutral/15">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 section-spacer-lg">
          <SectionLabel>What This Is Not</SectionLabel>

          <div className="mt-8 space-y-3">
            {[
              "Not a sponsored event.",
              "Not a pitch night.",
              "Not a founders' mixer.",
              "Not a content capture opportunity."
            ].map((item, i) => (
              <p key={i} className="text-vine-primary/60 text-[15px] pl-5 border-l-2 border-copper/25">{item}</p>
            ))}
          </div>

          <div className="mt-10 pl-5 border-l border-vine-neutral/20 space-y-2">
            <p className="text-vine-primary/45 text-[14px]">There are no panels.</p>
            <p className="text-vine-primary/45 text-[14px]">There are no name tags.</p>
            <p className="text-vine-primary/45 text-[14px]">There is no &ldquo;working the room.&rdquo;</p>
          </div>
        </div>
      </section>

      {/* ===== WHAT YOU ARE ACTUALLY BUYING ===== */}
      <section className="bg-vine-deep text-vine-warm">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 section-spacer-lg">
          <SectionLabel>What You Are Actually Buying</SectionLabel>

          <div className="mt-8">
            <p className="text-vine-neutral/60 text-[14px] font-medium mb-4">You are buying:</p>
            <BulletListLight items={[
              "Strategic proximity",
              "Pattern recognition across industries",
              "Honest peer-level perspective",
              "Signal in a confidential setting",
              "Access to operators who understand consequence"
            ]} />
          </div>

          <div className="mt-12 pt-8 border-t border-white/[0.08]">
            <p className="font-display text-xl text-vine-warm/80 font-light leading-relaxed">
              You are not buying dinner.
            </p>
            <p className="font-display text-xl text-copper font-light mt-1">
              You are buying perspective.
            </p>
          </div>
        </div>
      </section>

      {/* ===== WHO THIS TABLE IS FOR ===== */}
      <section className="bg-vine-warm section-spacer-lg">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 section-spacer-lg">
          <SectionLabel>Who This Table Is For</SectionLabel>

          <div className="mt-8">
            <p className="text-vine-primary/70 text-[14px] font-medium mb-4">This table works best for:</p>
            <BulletList items={[
              "Operators navigating scale or restructuring",
              "Founders making capital decisions",
              "Executives carrying real downside risk",
              "Leaders who want depth over exposure"
            ]} />
          </div>

          <div className="mt-10 p-6 border border-vine-neutral/15 bg-white/30">
            <p className="text-vine-primary/55 text-[15px]">If you want visibility, this is not the room.</p>
            <p className="text-vine-primary/75 text-[15px] font-medium mt-1">If you want sharper thinking, it is.</p>
          </div>
        </div>
      </section>

      {/* ===== THE ARC OF PARTICIPATION ===== */}
      <section className="bg-vine-deep text-vine-warm">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 section-spacer-lg">
          <SectionLabel>The Arc of Participation</SectionLabel>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 mt-14">
            {[
              {
                label: "First Dinner",
                text: "You observe the structure. You test the signal."
              },
              {
                label: "Third Dinner",
                text: "You notice patterns in your own decision-making."
              },
              {
                label: "Sixth Dinner",
                text: "You begin anticipating structural issues before they surface."
              }
            ].map((item) => (
              <div key={item.label}>
                <div className="flex items-center gap-4 mb-5">
                  <span className="text-copper text-[12px] tracking-[0.2em] uppercase font-medium">{item.label}</span>
                  <div className="flex-1 h-px bg-white/10" />
                </div>
                <p className="text-vine-neutral/70 text-[16px] leading-relaxed font-light">{item.text}</p>
              </div>
            ))}
          </div>

          <p className="mt-14 font-display text-2xl text-copper font-light">
            Proximity compounds.
          </p>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="bg-vine-warm section-spacer-lg">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 md:py-28 text-center">
          <div className="w-12 h-px bg-copper mx-auto mb-8" />
          <h2 className="font-display text-3xl text-vine-primary font-light">Join the table.</h2>
          <p className="mt-4 text-vine-primary/50 text-[15px]">Apply for the Advisory Circle + Dinner Passport.</p>
          <Link
            to="/apply"
            className="mt-8 inline-flex items-center gap-2 px-10 py-4 bg-copper text-white text-[13px] tracking-[0.12em] uppercase btn-copper"
          >
            Apply Now <ArrowRight size={15} />
          </Link>
        </div>
      </section>
    </div>
  );
}
